import { EDesignerEntityKind, ESdChangeReason, IScheduledJob, ViewPart } from "@kuika/kuika-cl-model";
import * as React from "react";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import {
  hasRecentlyModified,
  RECENTLY_MODIFIED_TAB,
  RECENTLY_MODIFIED_TAB_TEST_ID,
  resolveActiveTab,
  selectRecentlyModified
} from "../../../shared/utility/recently-modified";
import { buildLastModifiedColumn } from "../../../shared/utility/recently-modified-columns";
import {
  createFeedRequestGuard,
  haveItemIdsChanged,
  loadModificationFeed,
  LOADING_FEED,
  type IFeedRequestGuard,
  type IModificationFeed
} from "../../../shared/utility/recently-modified-feed";
import KuikaBadge from "../../ui-kit/kuika-badge/kuika-badge";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaEmptyState from "../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaFilterChips, { IKuikaFilterChipOption } from "../../ui-kit/kuika-filter-chips/kuika-filter-chips";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaListPage, {
  KuikaCreateCard,
  KuikaCreateCardRow,
  KuikaListPageHeader,
  KuikaListPageToolbar
} from "../../ui-kit/kuika-list-page/kuika-list-page";
import KuikaListTable, {
  KuikaListTableColumn,
  KuikaListTableNameCell,
  KuikaListTableRowAction,
  KuikaListTableStatusCell
} from "../../ui-kit/kuika-list-table/kuika-list-table";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaTemplateCard, { KuikaTemplateCardGrid } from "../../ui-kit/kuika-template-card/kuika-template-card";
import KuikaCodeText from "../../ui-kit/typography/CodeText/CodeText";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import { ScheduledJobNameModal } from "./scheduled-job-name-modal";
import { describeScheduledJobNextRun, IScheduledJobNextRun } from "./scheduled-job-next-run";
import { buildScheduledJobRowActions, isScheduledJobActive } from "./scheduled-job-row-actions";
import {
  describeCronValue,
  filterScheduledJobTemplates,
  IScheduledJobTemplate,
  IScheduledJobTemplateTag,
  SCHEDULED_JOB_TEMPLATE_CATALOG,
  scheduledJobTagRamp,
  scheduledJobTemplateTags,
  sortScheduledJobs
} from "./scheduled-job-templates";
import { describeTimeZone } from "./scheduled-job-time-zones";

/**
 * The Scheduled Jobs landing page: the project's jobs as a table, with ready-made
 * schedules behind a second tab.
 *
 * It replaces the Scheduled Jobs LEFT PANEL, so everything that panel could do lives here
 * — the list, its search and the search's persistence, activate/inactivate, delete, both
 * empty states, and creating a job. What the panel also did while a job was OPEN (moving
 * between jobs) is the editor header's switcher, because a landing page is not reachable
 * from inside an editor the way a resident panel was.
 *
 * Every mutation belongs to the canvas, which stays mounted while the editor is up; this
 * page only decides what to ask for.
 */

/** The tab holding every job, beside the "Recently modified" one the page opens on. */
const JOBS_TAB: string = "All Jobs";
const TEMPLATES_TAB: string = "Job Templates";

/** The id the jobs list has always emitted for a row, and for its actions trigger. */
const jobTestId = (name: string): string => name.replace(/\s/g, "");

export interface IScheduledJobsLandingProps {
  /** True while a create is in flight, so the cards can block a second click. */
  isCreating: boolean;
  /**
   * Where the create stands, for the name sheet's confirm. The canvas opens the new job's editor
   * once the ✓ has been seen, and that editor replaces this page — the sheet goes with it. A failed
   * create leaves the sheet open on "Try again".
   */
  createStatus: KuikaButtonStatus;
  /** True until the first fetch settles. */
  isListLoading?: boolean;
  /** Id of the row whose activate/inactivate/delete is in flight. */
  busyJobId?: string;
  /** Starts the create; `createStatus` reports how it goes. */
  onCreateBlank: (name: string) => void;
  onCreateFromTemplate: (template: IScheduledJobTemplate, name: string) => void;
  onOpenJob: (job: IScheduledJob) => void;
  onToggleActive: (job: IScheduledJob) => void;
  onRequestDelete: (job: IScheduledJob) => void;
}

interface IScheduledJobsLandingState {
  /** The tab the user chose, or null while the page is still deciding for itself. */
  pickedTab: string | null;
  feed: IModificationFeed;
  searchText: string;
  /** Selected template tags. Empty means every tag. */
  selectedTags: string[];
  pendingTemplate?: IScheduledJobTemplate;
  isNameModalVisible: boolean;
}

class ScheduledJobsLanding extends PureSdComponent<
  IScheduledJobsLandingProps & IWithSDProps,
  IScheduledJobsLandingState
> {
  private isMounted_: boolean = false;
  /** Guards `loadFeed` against an out-of-order reply when the item set changes twice in a row. */
  private feedGuard_: IFeedRequestGuard = createFeedRequestGuard();

  constructor(props: IScheduledJobsLandingProps & IWithSDProps) {
    super(props);
    this.state = {
      pickedTab: null,
      feed: LOADING_FEED,
      searchText: "",
      selectedTags: [],
      pendingTemplate: undefined,
      isNameModalVisible: false
    };
  }

  componentDidMount = (): void => {
    this.isMounted_ = true;
    // The panel rehydrated its query when the tool was re-entered; keep that.
    const searchText: string = this.props.sd.searchTexts[ViewPart.ScheduledJobsPanel];
    if (searchText && searchText.length > 0) {
      this.setState({ searchText });
    }
    void this.loadFeed();
  };

  componentWillUnmount = (): void => {
    this.isMounted_ = false;
  };

  /**
   * The page stays mounted through a create or a duplicate started elsewhere, so `scheduledJobs`
   * can gain or lose a row without this page ever remounting to re-fetch the feed on its own. The
   * id set is the smallest correct signal: it fires on a create/duplicate/delete and stays quiet
   * on an in-place rename, which this page reads off the row itself rather than off the feed.
   */
  componentDidUpdate = (prevProps: Readonly<IScheduledJobsLandingProps & IWithSDProps>): void => {
    const previousJobs: IScheduledJob[] = prevProps.sd?.scheduledJobs ?? [];
    if (haveItemIdsChanged(previousJobs, this.getJobs(), (row: IScheduledJob) => row.id)) {
      void this.loadFeed();
    }
  };

  /**
   * `IScheduledJob` carries `createdAt` and nothing else about time — when a job was last
   * touched can only come from the entity-modification feed.
   */
  loadFeed = async (): Promise<void> => {
    const token: number = this.feedGuard_.start();
    const projectId: string | undefined = this.props.sd?.project?.id;
    const feed: IModificationFeed = await loadModificationFeed([EDesignerEntityKind.ScheduledJob], projectId);
    if (!this.isMounted_ || !this.feedGuard_.isCurrent(token)) return;
    this.setState({ feed });
  };

  getRecentJobs = (): IScheduledJob[] =>
    selectRecentlyModified<IScheduledJob>({
      items: this.getFilteredJobs(),
      index: this.state.feed.index,
      getId: (row: IScheduledJob) => row.id,
      getName: (row: IScheduledJob) => row.name ?? "",
      now: Date.now()
    });

  /**
   * Derived rather than stored, so it cannot go stale against the feed: the page opens on the
   * recent tab, drops to the full list when nothing is recent or the feed failed, and otherwise
   * shows whatever the user picked.
   */
  getActiveTab = (): string =>
    resolveActiveTab({
      pickedTab: this.state.pickedTab,
      feedStatus: this.state.feed.status,
      // Asked of every job, not the searched ones: a query that matches nothing recent must not
      // move the user to another tab mid-keystroke.
      hasRecent: hasRecentlyModified<IScheduledJob>(
        this.getJobs(),
        this.state.feed.index,
        (row: IScheduledJob) => row.id,
        Date.now()
      ),
      recentTab: RECENTLY_MODIFIED_TAB,
      fallbackTab: JOBS_TAB
    });

  handleSearchChange = (searchText: string): void => {
    this.setState({ searchText });
    // Same store key the panel used, so a query survives leaving the tool and coming back.
    this.props.sd.setSearchTexts(ViewPart.ScheduledJobsPanel, searchText);
  };

  getJobs = (): IScheduledJob[] => this.props.sd?.scheduledJobs ?? [];

  /**
   * Matching is reproduced from the panel verbatim, transliteration quirk included: the
   * QUERY is transliterated, the candidate name is not. Changing it would silently change
   * which jobs a query surfaces.
   */
  getFilteredJobs = (): IScheduledJob[] => {
    const searchText: string = this.state.searchText;
    return sortScheduledJobs(
      this.getJobs().filter(
        (job: IScheduledJob): boolean =>
          job.name.toLowerCase().indexOf(KMainFunctions.turkishCharsToEnglish(searchText).toLowerCase()) !== -1
      )
    );
  };

  /**
   * Reproduced from the panel's `getSjobIsInactive`, which read `!sJob.json.isActive` —
   * so a job whose flag is missing counts as INACTIVE, not active. Creation always writes
   * `isActive: true`, so this only decides how malformed rows read; keeping the panel's
   * answer means the badge does not change meaning for anyone's existing data.
   */
  isJobActive = (job: IScheduledJob): boolean => isScheduledJobActive(job);

  getColumns = (): KuikaListTableColumn<IScheduledJob>[] => [
    {
      id: "name",
      header: () => "Name",
      accessorFn: (row: IScheduledJob) => row.name?.toLowerCase() ?? "",
      cell: (row: IScheduledJob) => (
        <span
          className="kuika-scheduled-jobs__name-cell"
          data-testid={jobTestId(row.name)}
          style={
            {
              "--kuika-list-table-icon-bg": "var(--blue-1)",
              "--kuika-list-table-icon-color": "var(--accent-fg)"
            } as React.CSSProperties
          }
        >
          <KuikaListTableNameCell
            name={row.name}
            icon={<KuikaSvg name="clock" width={14} height={14} />}
            secondary={describeCronValue(row.json?.cronValue)}
          />
        </span>
      )
    },
    {
      id: "schedule",
      header: () => "Schedule",
      // The zone rides in this cell as a badge rather than a column of its own: the table is
      // fixed-layout and Name takes whatever the fixed columns leave, so every column here is
      // width taken from the one column that says which job this is.
      width: 220,
      // Sorts on the sentence, which is what the column used to show and the name cell's
      // secondary line shows now — kept as the sort key so a header click still orders by
      // what the schedule actually means, not by the raw cron text underneath it.
      accessorFn: (row: IScheduledJob) => describeCronValue(row.json?.cronValue) ?? row.json?.cronValue ?? "",
      cell: (row: IScheduledJob) => {
        const cronValue: string | undefined = row.json?.cronValue;
        const timeZoneLabel: string | undefined = describeTimeZone(row.json?.timeZone);
        return (
          <span className="kuika-scheduled-jobs__schedule-cell">
            {cronValue ? (
              <KuikaCodeText className="kuika-scheduled-jobs__schedule-cron">{cronValue}</KuikaCodeText>
            ) : (
              <span className="kuika-list-table__muted">Not scheduled yet</span>
            )}
            {timeZoneLabel && (
              <KuikaBadge type="label" size="small" variant="subtle" color="grey" content={timeZoneLabel} />
            )}
          </span>
        );
      }
    },
    {
      id: "status",
      header: () => "Status",
      width: 110,
      accessorFn: (row: IScheduledJob) => (this.isJobActive(row) ? 0 : 1),
      cell: (row: IScheduledJob) => {
        const isActive: boolean = this.isJobActive(row);
        return (
          <KuikaListTableStatusCell
            label={isActive ? "Active" : "Inactive"}
            color={isActive ? "green" : "grey"}
            // Activate and Inactivate change this row's badge while the user watches it.
            animateChanges
            // A grey pill on its own does not say the job is dormant.
            hint={isActive ? undefined : "Paused. It will not run until you activate it."}
          />
        );
      }
    },
    {
      id: "nextRun",
      header: () => "Next run",
      width: 140,
      // Paused and undated rows sort on NO_NEXT_RUN_SORT_VALUE, so they collect at the end of an
      // ascending sort instead of scattering wherever their hidden 0/undefined would land.
      accessorFn: (row: IScheduledJob) => describeScheduledJobNextRun(row).sortValue,
      cell: (row: IScheduledJob) => {
        const nextRun: IScheduledJobNextRun = describeScheduledJobNextRun(row);
        return (
          <KuikaSmallText className="kuika-list-table__muted" title={nextRun.exact}>
            {nextRun.label}
          </KuikaSmallText>
        );
      }
    },
    {
      id: "created",
      header: () => "Created",
      width: 150,
      accessorFn: (row: IScheduledJob) => (row.createdAt ? new Date(row.createdAt).getTime() : 0),
      // A date only: the list carries no creator, and a user cell drew an anonymous avatar beside
      // Last modified's real one for the same person.
      // KuikaSmallText, like the shared Last modified cell beside it: a bare span took the table's
      // 14px while the other date read at 12px.
      cell: (row: IScheduledJob) => (
        <KuikaSmallText className="kuika-list-table__muted">
          {row.createdAt ? KMainFunctions.formatRelativeTime(row.createdAt) : "—"}
        </KuikaSmallText>
      )
    },
    buildLastModifiedColumn<IScheduledJob>(this.state.feed.index, (row: IScheduledJob) => row.id)
  ];

  getRowActions = (): KuikaListTableRowAction<IScheduledJob>[] =>
    buildScheduledJobRowActions({
      onToggleActive: this.props.onToggleActive,
      onRequestDelete: this.props.onRequestDelete
    });

  openNameModal = (template?: IScheduledJobTemplate): void => {
    this.setState({ isNameModalVisible: true, pendingTemplate: template });
  };

  closeNameModal = (): void => {
    this.setState({ isNameModalVisible: false, pendingTemplate: undefined });
  };

  /**
   * The sheet stays up through the whole create: its Create button runs, holds its ✓, and only
   * then does the canvas open the new job's editor, which replaces this page and the sheet with it.
   * It used to close on submit, before the request had even left, and hand the wait to a dimmed
   * page that did not say what it was waiting on.
   */
  handleNameSubmit = (name: string): void => {
    const template: IScheduledJobTemplate | undefined = this.state.pendingTemplate;
    if (template) {
      this.props.onCreateFromTemplate(template, name);
    } else {
      this.props.onCreateBlank(name);
    }
  };

  renderTemplateGallery = (): React.ReactNode => {
    const searched: IScheduledJobTemplate[] = filterScheduledJobTemplates(
      SCHEDULED_JOB_TEMPLATE_CATALOG,
      this.state.searchText
    );
    // Counts come off the searched set, so a chip never promises results the search has
    // already removed.
    const tags: IKuikaFilterChipOption[] = scheduledJobTemplateTags(searched).map(
      (tag: IScheduledJobTemplateTag): IKuikaFilterChipOption => ({
        label: tag.value,
        value: tag.value,
        count: tag.count
      })
    );
    const items: IScheduledJobTemplate[] =
      this.state.selectedTags.length > 0
        ? searched.filter((item: IScheduledJobTemplate): boolean => this.state.selectedTags.includes(item.tag))
        : searched;

    const chips: React.ReactNode = tags.length > 1 && (
      <KuikaFilterChips
        className="kuika-scheduled-jobs__tags"
        groupLabel="Template category"
        options={tags}
        value={this.state.selectedTags}
        onChange={(selectedTags: string[]): void => this.setState({ selectedTags })}
        allLabel="All"
        allCount={searched.length}
        data-testid="scheduledJobTemplateTagChips"
      />
    );

    if (items.length === 0) {
      return (
        <>
          {chips}
          <div className="kuika-scheduled-jobs__gallery-empty">
            <KuikaEmptyState
              variant="page"
              svgName="search-off"
              customName="No templates match your filters"
              description="Try a different search term, or clear the category filter."
            />
          </div>
        </>
      );
    }

    return (
      <>
        {chips}
        <KuikaTemplateCardGrid data-testid="scheduledJobTemplateGrid">
          {items.map((item: IScheduledJobTemplate) => (
            <KuikaTemplateCard
              key={item.id}
              // Tinted by tag, so twelve glyphs on one grey band do not read as twelve of
              // the same thing — and so the colour carries the category, not just variety.
              className={
                scheduledJobTagRamp(item.tag)
                  ? `kuika-scheduled-jobs__template--${scheduledJobTagRamp(item.tag)}`
                  : undefined
              }
              title={item.name}
              // The schedule IS the description here — a template's whole value is when it runs.
              description={`${describeCronValue(item.cronValue) ?? item.cronValue} · ${item.description}`}
              badge={item.tag}
              coverIcon={<KuikaSvg name={item.iconName} width={32} height={32} />}
              onClick={() => this.openNameModal(item)}
              primaryAction={{ label: "Use template", onClick: () => this.openNameModal(item) }}
              data-testid={`${item.id}TemplateCard`}
            />
          ))}
        </KuikaTemplateCardGrid>
      </>
    );
  };

  getEmptyTitle = (isNoResults: boolean, isEmptyRecents: boolean): string => {
    if (isNoResults) return "No scheduled jobs found";
    if (isEmptyRecents) return "Nothing changed recently";
    // Same shape as the other landings' empty titles ("No APIs yet"), not the bare tool name.
    return "No scheduled jobs yet";
  };

  getEmptyDescription = (isNoResults: boolean, isEmptyRecents: boolean): string => {
    if (isNoResults) return "We couldn't find any items matching your search.";
    if (isEmptyRecents) {
      // A failed feed is said out loud rather than shown as "nothing happened", which would be a
      // claim the page cannot actually make.
      return this.state.feed.status === "failed"
        ? "We couldn't load recent activity — open All Jobs to see every job."
        : "Jobs edited in the last 30 days appear here.";
    }
    return "No scheduled jobs have been added yet.";
  };

  renderTable = (): React.ReactNode => {
    const isRecentTab: boolean = this.getActiveTab() === RECENTLY_MODIFIED_TAB;
    const rows: IScheduledJob[] = isRecentTab ? this.getRecentJobs() : this.getFilteredJobs();
    const hasJobs: boolean = this.getJobs().length > 0;
    const isSearching: boolean = this.state.searchText.length > 0;
    // The "nothing here yet" state wins over the search miss, as it did in the panel.
    const isNoResults: boolean = hasJobs && isSearching;
    // The recent tab is only reached with nothing in it when the user picked it deliberately,
    // or when a search emptied it — the page opens on the full list otherwise.
    const isEmptyRecents: boolean = isRecentTab && rows.length === 0 && !isSearching;
    // The tab the page opens on has no rows until the feed lands, so it spins with the list
    // rather than claiming nothing has changed before it knows.
    const isFeedPending: boolean = isRecentTab && this.state.feed.status === "loading";

    return (
      <KuikaListTable<IScheduledJob>
        data={rows}
        columns={this.getColumns()}
        rowActions={this.getRowActions()}
        // The id the jobs list has always emitted for a row's menu, kept so the E2E suite
        // still targets one specific job's actions.
        rowActionsTestId={(row: IScheduledJob) => `${jobTestId(row.name)}ThreedotMenuButton`}
        rowKey={(row: IScheduledJob) => row.id}
        isRowBusy={(row: IScheduledJob) => row.id === this.props.busyJobId}
        // Spin only while there is nothing to show: a ready list is never held back, and
        // an empty one does not flash "nothing here" before the first fetch settles.
        loading={(!!this.props.isListLoading || isFeedPending) && rows.length === 0}
        onRowClick={(row: IScheduledJob) => this.props.onOpenJob(row)}
        emptyIcon={
          <KuikaSvg
            name={isNoResults ? "search-off" : "scheduled-jobs"}
            type={isNoResults ? "common" : "topbar-icons"}
            width={32}
            height={32}
          />
        }
        emptyVariant="page"
        emptyTitle={this.getEmptyTitle(isNoResults, isEmptyRecents)}
        emptyDescription={this.getEmptyDescription(isNoResults, isEmptyRecents)}
        data-testid="scheduledJobsTable"
      />
    );
  };

  render() {
    const isTemplatesTab: boolean = this.getActiveTab() === TEMPLATES_TAB;

    return (
      <>
        <KuikaListPage className="kuika-scheduled-jobs" data-testid="scheduledJobsScreen">
          {/* Named after the page's job — managing the jobs you already have — not after
              the create cards, which are one element within it. */}
          <KuikaListPageHeader
            icon={<KuikaSvg name="scheduled-jobs" type="topbar-icons" width={20} height={20} />}
            title="Scheduled Jobs"
            titleTestId="scheduledJobsTitle"
          />

          <KuikaCreateCardRow>
            <KuikaCreateCard
              title="Create a blank job"
              description="Pick a schedule yourself, then attach the actions it should run."
              icon={<KuikaSvg name="plus" width={20} height={20} />}
              iconBackground="var(--blue-1)"
              iconColor="var(--accent-fg)"
              onClick={() => this.openNameModal(undefined)}
              loading={this.props.isCreating}
              data-testid="newBlankScheduledJobButton"
            />
            <KuikaCreateCard
              title="From template"
              description="Start from a ready-made schedule — nightly, hourly, weekly or monthly."
              icon={<KuikaSvg name="repeat" width={20} height={20} />}
              iconBackground="var(--purple-1)"
              iconColor="var(--purple-5)"
              onClick={() => this.setState({ pickedTab: TEMPLATES_TAB })}
              data-testid="scheduledJobsFromTemplateCard"
            />
          </KuikaCreateCardRow>

          <KuikaListPageToolbar
            tabs={
              <KuikaTabs
                size="lg"
                // An option only needs the object form to carry a test id; the label is still
                // spelled out, because a labelless option renders as an empty pill.
                options={[
                  {
                    value: RECENTLY_MODIFIED_TAB,
                    label: RECENTLY_MODIFIED_TAB,
                    dataTestId: RECENTLY_MODIFIED_TAB_TEST_ID
                  },
                  { value: JOBS_TAB, label: JOBS_TAB, dataTestId: "allJobsTab" },
                  { value: TEMPLATES_TAB, label: TEMPLATES_TAB, dataTestId: "jobTemplatesTab" }
                ]}
                value={this.getActiveTab()}
                onChange={(value: string) => this.setState({ pickedTab: value })}
                dataTestId="scheduledJobsTabs"
              />
            }
            search={
              <KuikaInput
                size="md"
                placeholder="Search by name..."
                value={this.state.searchText}
                leftIcon={<KuikaSvg name="search" width={14} height={14} />}
                onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number) =>
                  this.handleSearchChange(String(value))
                }
                data-testid="searchInput"
                // The panel header this replaced offered one-click clearing; the toolbar
                // input keeps it rather than making the user select and delete.
                rightIcon={
                  this.state.searchText ? (
                    <button
                      type="button"
                      className="kuika-scheduled-jobs__search-clear"
                      aria-label="Clear search"
                      data-testid="clearSearchInputButton"
                      onClick={() => this.handleSearchChange("")}
                    >
                      <KuikaSvg name="x" width={12} height={12} />
                    </button>
                  ) : undefined
                }
              />
            }
          />

          {isTemplatesTab ? this.renderTemplateGallery() : this.renderTable()}
        </KuikaListPage>

        <ScheduledJobNameModal
          visible={this.state.isNameModalVisible}
          title={this.state.pendingTemplate ? "Create from template" : "Create a blank job"}
          subtitle={
            this.state.pendingTemplate
              ? `Your job starts on the template's schedule — ${
                  describeCronValue(this.state.pendingTemplate.cronValue) ?? this.state.pendingTemplate.cronValue
                }. You can change it any time.`
              : "Name the job, then pick its schedule and the actions it runs."
          }
          iconName={this.state.pendingTemplate ? this.state.pendingTemplate.iconName : "clock"}
          confirmLabel="Create"
          status={this.props.createStatus}
          initialValue={this.state.pendingTemplate?.defaultJobName}
          jobs={this.getJobs()}
          onCancel={this.closeNameModal}
          onSubmit={this.handleNameSubmit}
        />
      </>
    );
  }

  checkNeedRender = (_changeReason: ESdChangeReason): boolean => {
    return true;
  };
}

const scheduledJobsLanding = withSD<IScheduledJobsLandingProps>(ScheduledJobsLanding);
export { scheduledJobsLanding as ScheduledJobsLanding };
